import React, { useCallback, useState } from 'react';
import cn from 'classnames';
import { Link } from 'react-router-dom';

import { Logo } from './logo';
import s from './styles.module.css';

interface INavMenuItem {
  title: React.ReactNode;
  keepVisibleOnLargeScreen?: boolean;
  subItems?: INavMenuItem[];
  internalLink?: string;
  externalLink?: string;
  onClick?: () => void;
  onClose?: () => void;
  badge?: React.ReactNode;
  Component?: React.ElementType;
  isDisabled?: boolean;
  isUppercased?: boolean;
}

const useOpenState = () => {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  return { isOpen, handleToggle, handleClose };
};

interface INavMenuProps {
  appName: string | React.ReactNode;
  className?: string;
  menuItems: INavMenuItem[];
}

const NavMenuItem: React.VFC<INavMenuItem> = ({
  title,
  subItems,
  internalLink,
  externalLink,
  onClick,
  badge,
  Component,
  keepVisibleOnLargeScreen,
  onClose,
  isDisabled = false,
  isUppercased = false,
}) => {
  const handleClick = useCallback(() => {
    if (isDisabled) {
      return;
    }

    onClick?.();
    onClose?.();
  }, [isDisabled, onClick, onClose]);

  return (
    <li
      className={cn(s.item, {
        [s.hasSubItems]: !!subItems,
        [s.hiddenOnLargeScreen]: keepVisibleOnLargeScreen,
      })}
      data-test-id="main-menu-item"
    >
      {(() => {
        if (internalLink) {
          return (
            <Link
              to={internalLink}
              className={cn(s.itemInner, {
                [s.itemUppercased]: isUppercased,
                [s.isDisabled]: isDisabled,
              })}
              onClick={handleClick}
              data-test-id="main-menu-item__internal-link"
            >
              {title}
            </Link>
          );
        }

        if (externalLink) {
          return (
            <a
              href={isDisabled ? undefined : externalLink}
              target="_blank"
              rel="noreferrer"
              className={cn(s.itemInner, {
                [s.itemUppercased]: isUppercased,
                [s.isDisabled]: isDisabled,
              })}
              onClick={handleClick}
              data-test-id="main-menu-item__external-link"
            >
              {title}
            </a>
          );
        }

        if (subItems) {
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          return <SubNavMenu title={title} subItems={subItems} onClose={onClose} />;
        }

        if (Component) {
          return <Component className={s.itemInner} />;
        }

        return (
          <button
            className={cn(s.itemInner, {
              [s.itemUppercased]: isUppercased,
              [s.isDisabled]: isDisabled,
            })}
            onClick={handleClick}
            data-test-id="main-menu-item__button"
          >
            {title}
          </button>
        );
      })()}
      {badge}
    </li>
  );
};

interface ISubNavMenuProps {
  title: React.ReactNode;
  subItems: INavMenuItem[];
  onClose?: () => void;
}

const SubNavMenu: React.VFC<ISubNavMenuProps> = ({ title, subItems, onClose: onParentClose }) => {
  const { isOpen, handleToggle, handleClose } = useOpenState();

  return (
    <section
      className={s.subMenu}
      onMouseEnter={handleToggle}
      onMouseLeave={handleClose}
      data-test-id="main-menu-item__sub-menu"
    >
      <button
        className={cn(s.subHeader, { [s.isSubOpen]: isOpen })}
        data-test-id="main-menu-item__sub-menu__title"
      >
        {title}
      </button>
      <ul
        className={s.subMenuItems}
        onClick={handleClose}
        data-test-id="main-menu-item__sub-menu__content"
      >
        {subItems.map((subItem, idx) => (
          <NavMenuItem key={idx} {...subItem} onClose={onParentClose} />
        ))}
      </ul>
    </section>
  );
};

const NavMenu: React.VFC<INavMenuProps> = ({ appName, className, menuItems }) => {
  const { isOpen, handleToggle, handleClose } = useOpenState();

  const visibleItemsOnLargeScreen = menuItems.filter(menuItem => menuItem.keepVisibleOnLargeScreen);

  return (
    <header className={cn(s.root, className)} data-test-id="header">
      <div className={cn(s.right, { [s.isOpen]: isOpen })}>
        {visibleItemsOnLargeScreen.length > 0 && (
          <ul className={s.menuItemsOnLargeScreen} data-test-id="header__fixed-menu">
            {visibleItemsOnLargeScreen.map((item, idx) => (
              <NavMenuItem key={idx} {...item} onClose={handleClose} isUppercased={true} />
            ))}
          </ul>
        )}

        <button
          data-test-id="header__toggler-mobile"
          className={cn(s.toggleButton, s.mobile)}
          onClick={handleToggle}
        >
          <div className={s.toggleIcon} />
        </button>
        <button
          data-test-id="header__toggler-desktop"
          className={cn(s.toggleButton, s.desktop)}
          onMouseEnter={isOpen ? undefined : handleToggle}
          onClick={handleToggle}
        >
          <div className={s.toggleIcon} />
        </button>
      </div>
      <div className={s.logo} data-test-id="header__logo">
        {appName && <span className={s.appName}>{appName}</span>}
        <Logo />
      </div>

      <div className={s.navWrapper}>
        <nav className={s.menu} onMouseLeave={handleClose}>
          <ul className={s.menuItems} data-test-id="header__popup-menu">
            {menuItems.map((item, idx) => (
              <NavMenuItem key={idx} {...item} onClose={handleClose} />
            ))}
          </ul>
        </nav>
      </div>
    </header>
  );
};

export { NavMenu };
export type { INavMenuItem, INavMenuProps };
